import { Checkbox, FormattedDateTime, TableTd, TableTr } from '@customerio/pluma-components/react';
import type { Relationship } from '@customerio/hydra-ui-api';
import type { Customer } from '@/services/db/customer/entity';
import { formatNumberAndPluralize } from '@/utils/number';
import {
  RelationshipAttributeCountCell,
  RelationshipAttributeValueCell,
  RelationshipPersonCell,
  RelationshipViewCell,
} from './relationship-cells';

export type RelationshipRowModel = {
  key: string;
  relationship: Relationship;
  /** Position on the current page; the checkbox name, aria-label and range selection all key on it. */
  index: number;
  customer: Customer | undefined;
  personName: string | undefined;
  personHref: string | undefined;
  isSelected: boolean;
};

type RelationshipRowProps = {
  row: RelationshipRowModel;
  canSelect: boolean;
  /** The saved attribute columns; none shows the attribute count instead. */
  columns: readonly string[];
  onSelectedChange: (checked: boolean) => void;
  /** Opens this relationship's preview drawer. */
  onView: () => void;
};

export function RelationshipRow({ row, canSelect, columns, onSelectedChange, onView }: RelationshipRowProps) {
  const { relationship, index, personName, personHref } = row;
  const attributeCount = Object.keys(relationship.attributes).length;

  return (
    <TableTr data-test-relationships-table-entry>
      <TableTd py="0">
        <Checkbox
          name={`check_${index}`}
          isDisabled={!canSelect}
          isChecked={row.isSelected}
          onCheckedChange={onSelectedChange}
          ariaLabel={`Select relationship ${index}`}
        />
      </TableTd>
      <RelationshipPersonCell personName={personName} personHref={personHref} />
      {columns.length === 0 ? (
        <RelationshipAttributeCountCell
          label={formatNumberAndPluralize(attributeCount, 'attribute')}
          ariaLabel={`View relationship attributes for ${personName ?? `relationship ${index}`}`}
          onView={onView}
        />
      ) : (
        columns.map((column) => (
          <RelationshipAttributeValueCell
            key={column}
            column={column}
            value={relationship.attributes[column]}
            onView={onView}
          />
        ))
      )}
      <TableTd textAlign="right">
        <FormattedDateTime date={relationship.created_at} withRelativeDateFromNow withTooltip />
      </TableTd>
      <RelationshipViewCell onView={onView} />
    </TableTr>
  );
}
